import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject, OnChanges } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

// Models and services
import { 
  BrandResponse, 
  CreateBrandRequest, 
  UpdateBrandRequest,
} from './../../models/brand.model';
import { BrandService } from '../../services/brand.service';
import { ToastMessageService } from '@fitness-force/api';

@Component({
  selector: 'lib-brand-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule
],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '500px' }"
      (onHide)="onCancel.emit()">
    
      <ng-template pTemplate="header">
        <h3>{{ isEditMode ? ('RETAIL_BILLING.BRAND.EDIT' | translate) : ('RETAIL_BILLING.BRAND.CREATE' | translate) }}</h3>
      </ng-template>
    
      <!-- Level Type Header -->
      <div class="bg-primary-50 border-primary-100 border-1 border-round p-3 mb-4 flex align-items-center gap-2">
        <i class="pi pi-info-circle text-primary"></i>
        <span class="text-primary font-medium">
          {{ companyLevel() ? ('RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate) : ('RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate) }}
        </span>
      </div>
    
      <form [formGroup]="brandForm" class="grid">
        <!-- Brand Name (Primary Language) -->
        <div class="col-12">
          <label for="name_primary">{{ 'RETAIL_BILLING.BRAND.NAME' | translate }}
            @if (isSecondaryLanguage()) {
              <span>({{ primaryLanguage() }})</span>
            }
            <sup class="text-red-500">*</sup>
          </label>
          <input
            pInputText
            id="name_primary"
            formControlName="name_primary_language"
            class="w-full mt-2"
            aria-label="brand-name-primary-input" />
            @if (brandForm.get('name_primary_language')?.invalid && brandForm.get('name_primary_language')?.touched) {
              <small class="p-error">
                {{ 'RETAIL_BILLING.BRAND.NAME' | translate }} is required
              </small>
            }
          </div>
    
          <!-- Brand Name (Secondary Language) -->
          @if (isSecondaryLanguage()) {
            <div class="col-12">
              <label for="name_secondary">{{ 'RETAIL_BILLING.BRAND.NAME' | translate }}
                <span>({{ secondaryLanguage() }})</span>
                <sup class="text-red-500">*</sup>
              </label>
              <input
                pInputText
                id="name_secondary"
                formControlName="name_secondary_language"
                class="w-full mt-2"
                dir="rtl"
                aria-label="brand-name-secondary-input" />
                @if (brandForm.get('name_secondary_language')?.invalid && brandForm.get('name_secondary_language')?.touched) {
                  <small class="p-error">
                    {{ 'RETAIL_BILLING.BRAND.NAME' | translate }} is required
                  </small>
                }
              </div>
            }
          </form>
    
          <ng-template pTemplate="footer">
            <div class="flex justify-content-end gap-2">
              <p-button [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
                icon="pi pi-times"
                [text]="true"
                (onClick)="onCancel.emit()"
                aria-label="cancel-button" />
              <p-button [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
                icon="pi pi-check"
                [loading]="saving"
                [disabled]="brandForm.invalid"
                (onClick)="saveBrand()"
                aria-label="save-brand-button" />
            </div>
          </ng-template>
        </p-dialog>
    `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrandFormComponent implements OnInit, OnDestroy, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly brandService = inject(BrandService);
  private readonly toastService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly visible = input(false);
  readonly brand = input<BrandResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);
  readonly primaryLanguage = input.required<string>();
  readonly secondaryLanguage = input.required<string>();
  readonly isSecondaryLanguage = input(false);

  readonly visibleChange = output<boolean>();
  readonly onSave = output<BrandResponse>();
  readonly onCancel = output<void>();

  brandForm: FormGroup;
  saving = false;
  private destroy$ = new Subject<void>();

  get isEditMode(): boolean {
    return !!this.brand();
  }

  constructor() {
    this.brandForm = this.fb.group({
      name_primary_language: ['', Validators.required],
      name_secondary_language: [''],
      status: [true]
    });
  }

  ngOnInit() {
    this.updateFormValidation();
  }

  ngOnChanges() {
    const brand = this.brand();
    const visible = this.visible();
    if (visible && brand) {
      this.populateForm();
    } else if (visible && !brand) {
      this.resetForm();
    }
    //this.updateFormValidation();
  }

  updateFormValidation() {
    if (this.brandForm) {
      const nameSecondaryControl = this.brandForm.get('name_secondary_language');
      if (this.isSecondaryLanguage()) {
        nameSecondaryControl?.setValidators([Validators.required]);
      } else {
        nameSecondaryControl?.clearValidators();
      }
      nameSecondaryControl?.updateValueAndValidity();
    }
  }

  populateForm() {
    const brand = this.brand();
    if (brand) {
      this.brandForm.patchValue({
        name_primary_language: brand.name_primary_language || '',
        name_secondary_language: brand.name_secondary_language || '',
        status: brand.status
      });
    }
  }

  resetForm() {
    this.brandForm.reset({
      status: true
    });
  }

  saveBrand() {
    if (this.brandForm.valid) {
      this.saving = true;
      const formData = this.brandForm.value;

      const request: CreateBrandRequest | UpdateBrandRequest = {
        name_primary_language: formData.name_primary_language || '',
        name_secondary_language: formData.name_secondary_language || '',
        status: formData.status
      };

      const saveOperation = this.isEditMode
        ? this.brandService.updateCompanyBrand(this.brand()!.id, request as UpdateBrandRequest)
        : this.brandService.createCompanyBrand(request as CreateBrandRequest);

      saveOperation
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.saving = false;
            // Create a mock brand response for the event
            const savedBrand: BrandResponse = {
              ...this.brand(),
              id: this.brand()?.id || (response as any).id || 0,
              ...request,
              created_at: this.brand()?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: 1,
              updated_by: 1,
              company_id: 1
            } as BrandResponse;
            this.changeDetectorRef.detectChanges();
            this.onSave.emit(savedBrand);
            this.visibleChange.emit(false);
          },
          error: (error) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}